/**
 * Forgot Password Page
 * For HR/HRO users to request password reset
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../services/authService';

const ForgotPasswordPage = () => {
  const [employeeId, setEmployeeId] = useState('');
  const [contactMethod, setContactMethod] = useState('email'); // 'email' or 'phone'
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [mobileNumber, setMobileNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // OTP step state (email path only)
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const navigate = useNavigate();

  const countryCodeOptions = [
    { code: '+93', label: 'Afghanistan (+93)' },
    { code: '+355', label: 'Albania (+355)' },
    { code: '+213', label: 'Algeria (+213)' },
    { code: '+91', label: 'India (+91)' },
    { code: '+971', label: 'UAE (+971)' },
    { code: '+966', label: 'Saudi Arabia (+966)' },
    { code: '+65', label: 'Singapore (+65)' },
    { code: '+60', label: 'Malaysia (+60)' },
    { code: '+1', label: 'USA/Canada (+1)' },
    { code: '+44', label: 'UK (+44)' }
    // Add more as needed
  ];

  const startResendCountdown = () => {
    setResendCountdown(60);
    const t = setInterval(() => setResendCountdown(p => { if (p <= 1) { clearInterval(t); return 0; } return p - 1; }), 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (contactMethod === 'email') {
      if (!email) { setError('Email address is required'); return; }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) { setError('Please enter a valid email address'); return; }
    } else {
      if (!mobileNumber || mobileNumber.length < 4 || mobileNumber.length > 15) {
        setError('Please enter a valid mobile number (4-15 digits)'); return;
      }
    }

    setIsLoading(true);
    try {
      const formattedPhone = contactMethod === 'phone' ? `${countryCode}${mobileNumber}` : null;
      const emailAddress = contactMethod === 'email' ? email : null;
      const response = await authService.requestPasswordReset(employeeId, formattedPhone, emailAddress);

      if (response.success) {
        if (contactMethod === 'email') {
          setOtpSent(true);
          startResendCountdown();
        } else {
          // Phone path still uses link — show success screen
          setSuccess(true);
        }
      } else {
        setError(response.error || 'Failed to send reset instructions');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp || otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    if (!newPassword || newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }

    setIsLoading(true);
    try {
      const response = await authService.resetStaffPassword(email, otp, newPassword);
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const wrapperCls = "flex justify-center items-center min-h-screen bg-background p-lg relative overflow-hidden before:content-[''] before:absolute before:top-[-10%] before:left-[-5%] before:w-[300px] before:h-[300px] before:bg-accent-green before:rounded-full before:opacity-[0.06] before:animate-floatSlow before:z-0 after:content-[''] after:absolute after:bottom-[-10%] after:right-[-5%] after:w-[400px] after:h-[400px] after:bg-accent-terracotta after:rounded-full after:opacity-[0.06] after:animate-floatSlowReverse after:z-0";
  const cardCls = "bg-white p-2xl rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] w-full max-w-[420px] relative z-[1] border border-[rgba(46,46,46,0.08)] animate-slideUp";
  const inputCls = "w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] transition-all duration-200 box-border bg-surface text-text-primary focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)] disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60";
  const btnGreen = "w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none";

  if (success) {
    return (
      <div className={wrapperCls}>
        <div className={cardCls + " text-center"}>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-[24px] text-primary mb-sm font-semibold tracking-[-0.5px]">Password Reset!</h1>
          <p className="text-[15px] text-text-secondary mb-xl leading-[1.5]">
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <button className={btnGreen} onClick={() => navigate('/login')}>Back to Login</button>
        </div>
      </div>
    );
  }

  // OTP step — shown after OTP is sent for email path
  if (otpSent && contactMethod === 'email') {
    return (
      <div className={wrapperCls}>
        <div className={cardCls}>
          <h1 className="text-[24px] text-primary mb-sm text-center font-semibold tracking-[-0.5px]">Enter OTP</h1>
          <p className="text-[14px] text-text-secondary mb-xl text-center leading-[1.5]">
            A 6-digit OTP was sent to <strong>{email}</strong>. Enter it below along with your new password.
          </p>

          <form onSubmit={handleResetPassword} className="space-y-lg">
            <div>
              <label className="block mb-sm text-text-primary font-medium text-[14px]">OTP *</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                placeholder="6-digit OTP"
                disabled={isLoading}
                className={inputCls + " tracking-[0.4em] text-center font-semibold text-[20px]"}
              />
            </div>

            <div>
              <label className="block mb-sm text-text-primary font-medium text-[14px]">New Password *</label>
              <input type="password" value={newPassword} onChange={e => { setNewPassword(e.target.value); setError(''); }} placeholder="At least 6 characters" disabled={isLoading} className={inputCls} />
            </div>

            <div>
              <label className="block mb-sm text-text-primary font-medium text-[14px]">Confirm Password *</label>
              <input type="password" value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); setError(''); }} placeholder="Repeat password" disabled={isLoading} className={inputCls} />
            </div>

            {error && (
              <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md text-[14px] border-l-[3px] border-status-error leading-[1.5]">
                {error}
              </div>
            )}

            <button type="submit" disabled={isLoading} className={btnGreen}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>

            <p className="text-center text-[13px] text-text-tertiary">
              Didn't receive it?{' '}
              {resendCountdown > 0
                ? <span>Resend in {resendCountdown}s</span>
                : <button type="button" onClick={handleSendOtp} disabled={isLoading} className="text-accent-green underline font-medium">Resend OTP</button>
              }
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-background p-lg relative overflow-hidden
      before:content-[''] before:absolute before:top-[-10%] before:left-[-5%] before:w-[300px] before:h-[300px] 
      before:bg-accent-green before:rounded-full before:opacity-[0.06] before:animate-floatSlow before:z-0
      after:content-[''] after:absolute after:bottom-[-10%] after:right-[-5%] after:w-[400px] after:h-[400px] 
      after:bg-accent-terracotta after:rounded-full after:opacity-[0.06] after:animate-floatSlowReverse after:z-0">
      <div className="bg-white p-2xl rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] w-full max-w-[420px] 
        relative z-[1] border border-[rgba(46,46,46,0.08)] animate-slideUp">
        <h1 className="text-[28px] text-primary mb-sm text-center font-semibold tracking-[-0.5px]">
          Forgot Password
        </h1>
        <h2 className="text-[15px] text-text-secondary mb-xl text-center font-normal leading-[1.5]">
          Enter your email or mobile number to reset your password. Employee ID is optional for Admin/Chairman users.
        </h2>

        <form onSubmit={handleSendOtp}>
          <div className="mb-lg">
            <label htmlFor="employeeId" className="block mb-sm text-text-primary font-medium text-[14px]">
              Employee ID <span className="text-text-tertiary font-normal">(Optional for Admin/Chairman)</span>
            </label>
            <input
              type="text"
              id="employeeId"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              placeholder="JI201901"
              disabled={isLoading}
              autoFocus
              className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                transition-all duration-200 box-border bg-surface text-text-primary
                focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div className="mb-lg">
            <label className="block mb-sm text-text-primary font-medium text-[14px]">
              Contact Method
            </label>
            <div className="flex gap-2 mb-md">
              <button
                type="button"
                onClick={() => setContactMethod('email')}
                disabled={isLoading}
                className={`flex-1 py-[10px] px-4 border-[1.5px] rounded-md text-[14px] font-medium
                  transition-all duration-200 cursor-pointer
                  ${contactMethod === 'email' 
                    ? 'bg-accent-green text-surface border-accent-green' 
                    : 'bg-surface text-text-primary border-border hover:border-accent-green'}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => setContactMethod('phone')}
                disabled={isLoading}
                className={`flex-1 py-[10px] px-4 border-[1.5px] rounded-md text-[14px] font-medium
                  transition-all duration-200 cursor-pointer
                  ${contactMethod === 'phone' 
                    ? 'bg-accent-green text-surface border-accent-green' 
                    : 'bg-surface text-text-primary border-border hover:border-accent-green'}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Mobile Number
              </button>
            </div>

            {contactMethod === 'email' ? (
              <div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  disabled={isLoading}
                  className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                    transition-all duration-200 box-border bg-surface text-text-primary
                    focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                    disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                />
                <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
                  Enter your registered email address
                </small>
              </div>
            ) : (
              <div>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    disabled={isLoading}
                    className="w-[110px] py-[14px] px-3 border-[1.5px] border-border rounded-md text-[15px] 
                      transition-all duration-200 box-border bg-surface text-text-primary cursor-pointer
                      focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                      disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {countryCodeOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="mobileNumber"
                    value={mobileNumber}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, '');
                      if (onlyDigits.length <= 15) {
                        setMobileNumber(onlyDigits);
                      }
                    }}
                    placeholder="Enter phone number"
                    disabled={isLoading}
                    className="flex-1 py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                      transition-all duration-200 box-border bg-surface text-text-primary
                      focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                      disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </div>
                <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
                  Enter your mobile number (4-15 digits)
                </small>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg 
              text-[14px] border-l-[3px] border-status-error leading-[1.5]">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
              text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
              hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
              active:translate-y-0 active:enabled:translate-y-0
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Instructions'}
          </button>
        </form>

        <div className="mt-xl pt-lg border-t border-border-light text-center">
          <p className="text-text-secondary text-[14px] mb-md">
            Remember your password?
          </p>
          <Link
            to="/login"
            className="inline-block w-full py-[14px] px-6 bg-surface text-accent-green border-[1.5px] border-accent-green 
              rounded-md text-[15px] font-semibold cursor-pointer transition-all duration-200 text-center no-underline
              hover:bg-accent-green hover:text-surface hover:-translate-y-[1px] hover:shadow-md
              active:translate-y-0"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;